// What Jeremy actually decides about a suggestion.
//
// ── WHAT THIS REPLACES, AND WHY ───────────────────────────────────────────
// The inbox asked the owner of the company for: four status buttons, a written
// answer, a publish button, a work title, an engineering brief, a list of files
// and folders Cole may touch, and two more buttons to queue and approve it.
// Eleven controls to answer one question. Most of them existed because the
// BACKEND needed the values, which makes them HQ's job, not his.
//
// He decides three things, and only these three:
//
//   Approve        — do it. HQ works out what "doing it" involves.
//   Deny           — do not, and why not in one line. The reason is a business
//                    judgement nobody else holds, which is why it is the one
//                    piece of typing that stays.
//   Ask a question — he needs something only the person who wrote it knows.
//
// Everything after Approve is an employee's job: which files are involved, what
// the change is, whether it is a change at all, when it is ready to look at.
//
// ── THE PUBLIC RESTATEMENT IS NOT HIS JOB EITHER ──────────────────────────
// An anonymous suggestion cannot be answered by quoting it back — the wording
// can identify who wrote it — so the loop module requires a restatement before
// publishing. That was a textarea on the screen. It is derivable: the category
// says what the ask is about without quoting a word of it.
//
// Pure. No database, no clock, no fetch.

import type { SuggestionStatus } from "./suggestion-loop";

export const SUGGESTION_DECISION_VERSION = "suggestion-decision-v0.1";

export type Decision = "approve" | "deny" | "ask" | "resume";

export interface DecidableSuggestion {
  id: string;
  status: SuggestionStatus;
  content: string;
  category: string | null;
  is_anonymous: boolean;
  author_id: string | null;
}

export interface DecisionVerdict {
  allowed: boolean;
  /** Why not, in plain English. Null when allowed. */
  reason: string | null;
}

const ok: DecisionVerdict = { allowed: true, reason: null };
const no = (reason: string): DecisionVerdict => ({ allowed: false, reason });

/** Statuses that mean somebody already decided this. */
const DECIDED: readonly SuggestionStatus[] = ["planned", "implemented", "wont_do"];

export function decisionVerdict(
  s: DecidableSuggestion,
  decision: Decision,
  text: string,
): DecisionVerdict {
  if (decision === "deny") {
    // The one thing only he knows. One line, not an essay — but not nothing,
    // because a refusal with no reason is what stops the next suggestion.
    if (!text.trim()) return no("Say why not, in a line. They will read it.");
    if (s.status === "wont_do") return no("This was already declined.");
    return ok;
  }

  if (decision === "ask") {
    if (!text.trim()) return no("Type the question first.");
    // Nobody to ask. Publishing a question at an anonymous submitter is a
    // reasonable thing to want, and it is not what this button does.
    if (s.is_anonymous || !s.author_id) {
      return no("This was sent anonymously, so there is nobody to ask. Approve or decline it on what it says.");
    }
    return ok;
  }

  if (DECIDED.includes(s.status)) {
    return no(
      s.status === "wont_do"
        ? "This was declined. Reopening it is not something this screen does yet."
        : "This is already approved.",
    );
  }
  return ok;
}

/**
 * The answer the submitter reads, written by HQ so he does not have to.
 *
 * Approval says what will happen next in the words of somebody who does not
 * know what a pull request is. It deliberately does not promise a date.
 */
export function answerFor(decision: Decision, text: string): string {
  if (decision === "deny") return text.trim();
  if (decision === "ask") return text.trim();
  return "Approved. Cole is looking into this now, and you will hear back here when there is "
    + "something to see. If it turns out this is already how it works, we will say so.";
}

/**
 * A restatement safe to publish where everyone can read it.
 *
 * Only for anonymous suggestions, and never a word of the original: the
 * category is what it is about, and it identifies nobody.
 */
export function publicSummaryFor(s: DecidableSuggestion): string | null {
  if (!s.is_anonymous && s.author_id) return null;
  const about = s.category?.trim();
  return about ? `A request about ${about.toLowerCase()}` : "An anonymous request";
}

/** Where the suggestion lands once he has decided. */
export function statusFor(decision: Decision): SuggestionStatus | null {
  if (decision === "approve") return "planned";
  if (decision === "deny") return "wont_do";
  // A question is not a decision — but it is also not "read, not yet decided",
  // which is what 'reviewed' says and what this used to return. The ball is
  // with the author, and the status now says so (HQ-SUGGEST-001). It stays open
  // and stays his to come back to either way.
  return "awaiting_author";
}

/**
 * Should this approval become engineering work?
 *
 * ── WHY THIS IS NOT A QUESTION FOR JEREMY ─────────────────────────────────
 * "Is this a code change?" is a judgement about a codebase, which is exactly
 * the kind of thing he should never be asked. So HQ hands every approval to
 * Cole to LOOK at, and Cole answers it himself: he reports "nothing to change
 * here and this is why" as readily as he reports a fix. A wrong guess costs one
 * cheap read-only investigation; a question on the screen costs the owner of the
 * company his attention every time.
 */
export const APPROVAL_STARTS_INVESTIGATION = true;

/**
 * Where Cole starts looking when nobody has told him where to look.
 *
 * ── WHY THIS IS NOT A FIELD ON THE SCREEN ─────────────────────────────────
 * It used to be, and asking the owner which folders an engineer may open is the
 * clearest example of the backend's need becoming his work. He does not know,
 * he should not have to learn, and Cole is the one who can find out — his
 * investigation lists these directories and picks what is relevant, which is
 * what that step was built to do.
 *
 * These are the areas HQ itself is made of. Investigation is read-only; the
 * change step is bounded afterwards to the files Cole actually identified, so a
 * broad place to look does not become broad permission to edit.
 */
export const DEFAULT_INVESTIGATION_SCOPE: readonly string[] = [
  "hq/src/pages",
  "hq/src/lib",
  "hq/src/components",
  "hq/netlify/functions",
];

/** What Cole is told, built from the suggestion rather than typed by anybody. */
export function briefFor(s: DecidableSuggestion, answer: string): string {
  return [
    "Somebody at Scenta asked for this:",
    "",
    s.content.trim(),
    "",
    "Jeremy approved it. Work out whether it is a change to HQ, and if it is, what the",
    "smallest correct version of it looks like.",
    "",
    "If it already works this way, say so plainly and change nothing — that is a real",
    "answer and the one that saves everybody the most time.",
    "",
    `They were told: "${answer}"`,
  ].join("\n");
}
